import React from 'react';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Card from '@amzn/meridian/card';
import DatePicker from '@amzn/meridian/date-picker';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import {
  RANGE_PRESETS,
  RangePreset,
  ReportWindowFilters,
  SHIFT_OPTIONS,
} from './useImageQualityFilters';

/** The 7d / 14d / 28d range presets; a custom From/To range (preset null) selects none. */
export const RangePresetButtons: React.FC<{ filters: ReportWindowFilters }> = ({ filters }) => (
  <ButtonGroup
    value={filters.preset ?? ''}
    onChange={(preset: RangePreset) => filters.applyPreset(preset)}
    aria-label="Date range"
    data-testid="iq-presets"
  >
    {RANGE_PRESETS.map(preset => (
      <ButtonOption key={preset.key} value={preset.key} data-testid={`iq-preset-${preset.key}`}>
        {preset.label}
      </ButtonOption>
    ))}
  </ButtonGroup>
);

/** From/To dates, 7d/14d/28d presets and the shift filter; shared by Image Quality and the scorecards. */
const ReportWindowBar: React.FC<{ filters: ReportWindowFilters; children?: React.ReactNode }> = ({
  filters,
  children,
}) => (
  <Card spacingInset="400 500">
    <Row alignmentHorizontal="justify" alignmentVertical="center" wrap="down" spacing="400">
      <Row spacing="300" alignmentVertical="center" wrap="down">
        <Text tag="label" type="b300" color="secondary">
          From
        </Text>
        <DatePicker
          value={filters.startDate}
          onChange={filters.onFromChange}
          disabledDates={filters.isDateDisabled}
          width={180}
          size="small"
          data-testid="iq-from"
        />
        <Text tag="label" type="b300" color="secondary">
          To
        </Text>
        <DatePicker
          value={filters.endDate}
          onChange={filters.onToChange}
          disabledDates={filters.isDateDisabled}
          width={180}
          size="small"
          data-testid="iq-to"
        />
        <RangePresetButtons filters={filters} />
      </Row>
      <Row spacing="300" alignmentVertical="center">
        {children}
        <Text tag="label" type="b300" color="secondary">
          Shift
        </Text>
        <Select
          value={filters.shift}
          onChange={filters.setShift}
          width={150}
          size="small"
          data-testid="iq-shift-select"
        >
          {SHIFT_OPTIONS.map(opt => (
            <SelectOption key={opt.value} value={opt.value} label={opt.label} />
          ))}
        </Select>
      </Row>
    </Row>
  </Card>
);

export default ReportWindowBar;
